import { RefreshOutlined } from '@mui/icons-material';
import { useState } from 'react';
import styles from '@styles/components/General.module.css';
import designSystem from '@styles/designSystem';
import { isMobile } from 'react-device-detect';

interface ButtonProps {
    text: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
    hollowed?: boolean;
    backgroundColor?: string;
    textColor?: string;
    borderColor?: string;
    width?: string;
    height?: string;
    children?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({ 
		text, 
		onClick,
		disabled = false,
		loading = false,
		backgroundColor, 
		textColor,
        borderColor,
        width = designSystem.sizeButtonWidth,
        height = designSystem.sizeButtonHeight,
        children
	}) => {

    const customStyle = {
		backgroundColor: backgroundColor,
		color: textColor,
		borderColor: borderColor,
        height: height,
        width: width
	};

	const disabledStyle = {
		backgroundColor: 'transparent',
		color: designSystem.colorDisabledBackground,
		border: '1px solid '+designSystem.colorDisabledBackground,
		cursor: "not-allowed",
        height: height,
        width: width
	};

	// When loading, don't apply the disabledStyle
	const appliedStyle = disabled && !loading ? disabledStyle : customStyle;

	return (
		<div 
			className={styles.button} 
			onClick={disabled || loading ? () => {} : onClick} 
			style={appliedStyle}
		>
			{loading && <span className={styles.skeleton}></span>}
			{!loading && text}
            {children}
		</div>
	);
};


interface SpecializedButtonProps extends Omit<ButtonProps, 'backgroundColor' | 'textColor' | 'borderColor'> {}

export const HollowedButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            backgroundColor='transparent'
            textColor='var(--color-main-action)'
            borderColor='var(--color-main-action)'
        />
    );
};


export const NormalButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            backgroundColor='var(--color-main-action)'
            textColor='white'
            borderColor='var(--color-main-action)'
        />
    );
};


export const TintedButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            backgroundColor='#ec6c561a'
            textColor='var(--color-main-action)'
            borderColor='#ec6c561a'
        />
    );
};

export const GrayButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            backgroundColor='var(--color-dropdown-background)'
            textColor='var(--color-dropdown-text)'
            borderColor='var(--color-dropdown-background)'
        />
    );
};

export const GhostButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            backgroundColor='transparent'
            textColor='var(--color-main-action)'
            borderColor='var(--color-dropdown-background)'
        />
    );
};


export const RefreshButton: React.FC<SpecializedButtonProps> = (props) => {
    const [isRotating, setIsRotating] = useState(false);

    const handleClick = () => {
        setIsRotating(true);
        props.onClick?.();
        setTimeout(() => setIsRotating(false), 500);
    };

    return (
        <Button
            {...props}
            textColor='var(--color-main-action)'
            borderColor='transparent'
            onClick={handleClick}
            width={isMobile ? designSystem.sizeIconWidth : designSystem.sizeButtonHeight}
        >
            <div className={`${styles['refresh-button']} ${isRotating ? styles.rotate : ''}`}>
                <RefreshOutlined style={{ 
                    color: designSystem.colorMainAction, 
                    fontSize: isMobile ? '24' : '32'
                }} />
            </div>
        </Button>
    );
};

export default Button;